import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ModalStyled, ToastStyled } from 'src/components';
import {
  useApproveCart,
  useCurrentUser,
  useGetCartQuery,
  useSubmitCart,
} from 'src/hooks';
import ApproveIcon from 'src/images/icons/cart-approved-icon.svg?react';
import type { CartApprovalStatus, OrgMember } from 'src/types';
import { InitialView } from './InitialView';
import { NamedApproversView } from './NamedApproversView';
import { SentForApprovalView } from './SentForApprovalView';

type Props = {
  cartId: string;
  show: boolean;
  setShow: (value: boolean) => void;
  handleContinue?: (skipApproval: boolean) => void;
  isDirty?: boolean;
  resetIsDirty?: () => void;
};

export const RequestApprovalModal = ({
  cartId,
  show,
  setShow,
  handleContinue,
  isDirty,
  resetIsDirty,
}: Props) => {
  const { user } = useCurrentUser();
  const [step, setStep] = useState<
    'initial' | 'namedApprovers' | 'sentForApproval'
  >('initial');
  const [answer, setAnswer] = useState<'unknown' | 'required' | 'notRequired'>(
    'unknown',
  );
  const [selectedApprover, setSelectedApprover] = useState<OrgMember | null>(
    null,
  );

  const cartQuery = useGetCartQuery({ cartId });
  const currentCTARef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const { mutateAsync: submitCart } = useSubmitCart();

  const { mutateAsync: approveCart } = useApproveCart({
    cartId,
  });

  // we set approvalLayer to 0 here because it could not exist yet
  const currentLayer =
    cartQuery.data?.approvalDetails?.at(-1)?.approvalLayer || 0;
  const isInitialLayer = currentLayer === 0;
  const currentStatus: CartApprovalStatus | undefined =
    cartQuery.data?.approvalDetails?.at(-1)?.status;

  const {
    namedApprovers: shouldShowNamedApprovers,
    multiLayerApproval: shouldUseMultiLayerApproval,
  } = user?.subscription.orgSettings || {
    namedApprovers: false,
    multiLayerApproval: false,
  };

  const shakeCurrentCTA = () => {
    currentCTARef.current?.classList.add('animate-shake');
    setTimeout(() => {
      currentCTARef.current?.classList.remove('animate-shake');
    }, 1000);
  };

  // answer gets reset when the modal is closed
  const handleClose = async (xButtonClose = false) => {
    const showToast = xButtonClose && currentLayer !== 0;
    // if the user needs to answer the approval modal before closing the
    // browser/modal, then isDirty is true. in this case, we shake the
    // questions based on what point in the form we are at
    if (isDirty && !xButtonClose) {
      shakeCurrentCTA();
      return;
    }

    setShow(false);

    // wait for the modal animation to finish before resetting the state
    await new Promise((resolve) => setTimeout(resolve, 150));

    setAnswer('unknown');
    setStep('initial');
    setSelectedApprover(null);

    resetIsDirty?.();

    if (showToast) {
      toast.custom((toastBlock) => (
        <ToastStyled
          variant='warning'
          toastTitle='Heads Up!'
          handleClose={() => toast.dismiss(toastBlock)}
        >
          {isInitialLayer
            ? 'Cart has not been submitted for approval.'
            : 'Your response has not been sent. Please try again.'}
        </ToastStyled>
      ));
    }
  };

  const handleClickPrimaryAction = async () => {
    if (step === 'initial') {
      if (currentStatus === 'rejected') {
        if (answer === 'notRequired') {
          handleContinue?.(true);
        } else {
          if (shouldShowNamedApprovers) {
            setStep('namedApprovers');
          } else {
            await submitAndOrApproveCart(true, false);
          }
        }

        return;
      }

      if (answer === 'required') {
        // show named approvers or simply send to all approvers/admins
        if (shouldShowNamedApprovers) {
          setStep('namedApprovers');
          return;
        }

        await submitAndOrApproveCart(true, true);
      } else {
        // if we're on a layer, then we simply approve the cart and we're done. Otherwise, we load the cart
        // because the user is saying the cart doesn't need approval.
        if (currentLayer > 0) {
          await submitAndOrApproveCart(false, true);
        } else if (handleContinue) {
          handleContinue(true);
        } else {
          console.error("RequestApprovalModal: shouldn't be here.");
        }
      }
    } else if (step === 'namedApprovers') {
      if (!selectedApprover) {
        return;
      }

      await submitAndOrApproveCart(true, currentStatus !== 'rejected', {
        approverId: selectedApprover.id,
        approverEmail: selectedApprover.email,
        approverName: selectedApprover.name,
      });
    }

    resetIsDirty?.();

    // if the cart's been rejected then we are currently resubmitting it
    // so we don't want to close the modal since we will show the 'sent for approval' view.
    if (currentStatus && currentStatus === 'rejected') {
      return;
    }

    // if the cart's been approved or we are on a layer other than the initial layer, then we can close the modal
    // since we will show the 'Review Approved' view.
    if (currentLayer !== 0) {
      handleClose();
    }
  };

  // TODO - this will create issues if the approveCart query succeeds but the submitCart query fails.
  // Will need to fix in the future.
  const submitAndOrApproveCart = async (
    shouldSubmitCart = true,
    shouldApproveCart: boolean,
    approverDetails?: {
      approverId: string;
      approverEmail: string;
      approverName: string;
    },
  ) => {
    if (shouldUseMultiLayerApproval && currentLayer > 0 && shouldApproveCart) {
      await approveCart({
        layer: currentLayer,
        emailOriginalRequester: !shouldSubmitCart,
      });
    }

    if (shouldSubmitCart) {
      await submitCart({
        cartId,
        layer: currentLayer + 1,
        ...(approverDetails && { ...approverDetails }),
      });
    }

    if (currentLayer === 0 || currentStatus === 'rejected') {
      setStep('sentForApproval');
    } else {
      navigate(
        `/review/${cartId}/approved?additional_approval=${shouldSubmitCart}`,
      );
    }
  };

  return (
    <ModalStyled open={show} onClose={handleClose}>
      <div className='relative flex flex-col items-center justify-center gap-6 p-8'>
        <ApproveIcon className='h-auto w-[150px]' />

        {step === 'initial' ? (
          <InitialView
            ref={currentCTARef}
            answer={answer}
            setAnswer={setAnswer}
            handleClickPrimaryAction={handleClickPrimaryAction}
            shouldShowNamedApprovers={shouldShowNamedApprovers}
            isInitialLayer={isInitialLayer}
            currentStatus={currentStatus}
          />
        ) : step === 'namedApprovers' ? (
          <NamedApproversView
            ref={currentCTARef}
            handleClickPrimaryAction={handleClickPrimaryAction}
            selectedApprover={selectedApprover}
            setSelectedApprover={setSelectedApprover}
          />
        ) : step === 'sentForApproval' ? (
          <SentForApprovalView
            handleClose={handleClose}
            namedApproversEnabled={shouldShowNamedApprovers}
            selectedApprover={selectedApprover}
          />
        ) : null}
      </div>
    </ModalStyled>
  );
};
